import {
  BOARD_STATUSES,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '../../api/organizer';

/**
 * Today as a YYYY-MM-DD calendar date in the browser's timezone.
 *
 * Due dates are calendar dates, so they must be compared as strings against a
 * local day. `toISOString().slice(0, 10)` would give the UTC day and quietly
 * mark things overdue an hour early in Brussels every evening.
 */
export function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA').format(new Date());
}

/** Statuses that still count as work in progress. */
const OPEN_STATUSES: readonly TaskStatus[] = ['todo', 'in_progress', 'blocked'];

export function isOpen(task: Task): boolean {
  return OPEN_STATUSES.includes(task.status);
}

export function isOverdue(task: Task, today = todayIso()): boolean {
  return isOpen(task) && task.dueDate !== null && task.dueDate < today;
}

export function isDueToday(task: Task, today = todayIso()): boolean {
  return isOpen(task) && task.dueDate === today;
}

/** Tasks grouped into board columns, each in rank order so drag-to-reorder sticks. */
export function groupByStatus(tasks: Task[]): Record<TaskStatus, Task[]> {
  const groups = Object.fromEntries(BOARD_STATUSES.map((s) => [s, [] as Task[]])) as Record<
    TaskStatus,
    Task[]
  >;
  for (const task of tasks) {
    if (groups[task.status]) groups[task.status]!.push(task);
  }
  for (const status of BOARD_STATUSES) {
    groups[status] = sortByRank(groups[status] ?? []);
  }
  return groups;
}

/** Rank order, with the same stable tiebreak the server uses. */
export function sortByRank(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank < b.rank ? -1 : 1;
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  return Math.round((to - from) / 86_400_000);
}

/**
 * A short human label for a due date — "Today", "Tomorrow", "3d late".
 *
 * Relative wording only holds attention inside about a week; past that an actual
 * date is more useful than "in 34 days".
 */
export function dueLabel(dueDate: string | null, today = todayIso()): string | null {
  if (!dueDate) return null;
  const delta = daysBetween(today, dueDate);

  if (delta === 0) return 'Today';
  if (delta === 1) return 'Tomorrow';
  if (delta === -1) return '1d late';
  if (delta < 0) return `${Math.abs(delta)}d late`;
  if (delta <= 7) return `in ${delta}d`;

  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
    new Date(`${dueDate}T00:00:00Z`),
  );
}

/** Tailwind classes for a due chip, so lateness reads at a glance. */
export function dueToneClasses(dueDate: string | null, today = todayIso()): string {
  if (!dueDate) return 'text-text-tertiary';
  if (dueDate < today) return 'text-error font-medium';
  if (dueDate === today) return 'text-warning font-medium';
  return 'text-text-tertiary';
}

/**
 * Priority dots step down in ink strength. Only urgent borrows a status colour
 * (red), because urgency is a status; high must not reuse the amber "warning".
 */
export const PRIORITY_DOT_CLASSES: Record<TaskPriority, string> = {
  urgent: 'bg-error',
  high: 'bg-text-primary',
  medium: 'bg-text-muted',
  low: 'bg-border',
};

/** Sort order for the list view: soonest due first, then priority, then rank. */
const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function sortForList(tasks: Task[], today = todayIso()): Task[] {
  return [...tasks].sort((a, b) => {
    // Overdue work leads. Due-date order already implies it; this keeps it true
    // if the order below ever changes.
    const aLate = isOverdue(a, today);
    const bLate = isOverdue(b, today);
    if (aLate !== bLate) return aLate ? -1 : 1;
    // Undated work sinks below dated work rather than jumbling in with it.
    if ((a.dueDate === null) !== (b.dueDate === null)) return a.dueDate === null ? 1 : -1;
    if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
    if (a.priority !== b.priority) return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
    return a.rank < b.rank ? -1 : 1;
  });
}

/**
 * The neighbours a dropped card lands between, from a column's post-drop order.
 *
 * The API takes ids rather than an index because the client's column may be a
 * moment stale, and ids still name the intended gap when it is.
 */
export function neighboursAt(
  column: Task[],
  index: number,
  movingTaskId: string,
): { beforeId: string | null; afterId: string | null } {
  const without = column.filter((t) => t.id !== movingTaskId);
  return {
    beforeId: without[index - 1]?.id ?? null,
    afterId: without[index]?.id ?? null,
  };
}

/**
 * One number per call: "4 waiting". The found count shows only when it says
 * something more ("4 found · 3 waiting", "4 found, all reviewed").
 */
export function callCountLabel(found: number, waiting: number | undefined): string {
  if (waiting === undefined) return `${found} found`;
  if (waiting === found) return `${waiting} waiting`;
  if (waiting === 0) return `${found} found, all reviewed`;
  return `${found} found · ${waiting} waiting`;
}
